import { useMemo, useState } from 'react';
import { useAuth } from '@contexts/auth';
import { useMetadata } from '@contexts/org-metadata';
import { metadataHook } from '@hooks/metadata';
import {
  getMarketplaceIcon,
  type MarketplaceIconData,
  type OrgMarketplace,
} from '@utility/marketplace';
import { Tooltip, cn } from '@skupreme/shared/ui';
import { flagImages } from '../../legacy-bridge';

export type MarketplaceAvatarProps = {
  marketplace?: (MarketplaceIconData & { region_name?: string }) | null;
  /** Drawn when neither the marketplace nor the org has a logo. */
  fallbackText?: string;
  /** The storefront this order came from, shown on hover. */
  shopName?: string;
  /** Greys the avatar out while the order is being processed. */
  isDimmed?: boolean;
  size?: 'md' | 'lg';
  className?: string;
};

function initial(...candidates: (string | undefined)[]): string {
  const named = candidates.find(candidate => candidate?.trim());

  return named ? named.trim()[0].toUpperCase() : '?';
}

function flagSrc(region: string | undefined): string | undefined {
  if (!region) {
    return undefined;
  }

  return flagImages[region.toUpperCase()];
}

/**
 * The marketplace logo with the storefront's region on its corner. Falls back
 * to the org logo, then to an initial.
 */
export function MarketplaceAvatar({
  marketplace,
  fallbackText,
  shopName,
  isDimmed,
  size = 'md',
  className,
}: MarketplaceAvatarProps) {
  const [failed, setFailed] = useState(false);
  const { metadata } = metadataHook();
  const { currentOrgInfo } = useAuth();
  const orgMetadata = useMetadata() as
    | { marketplaces?: OrgMarketplace[] }
    | undefined;

  const src = useMemo(
    () =>
      getMarketplaceIcon(
        marketplace?.type || '',
        marketplace,
        metadata,
        orgMetadata?.marketplaces,
      ),
    [marketplace, metadata, orgMetadata],
  );

  const orgLogo = currentOrgInfo?.logo?.url;
  const shown = failed ? orgLogo : src || orgLogo;
  const region = flagSrc(marketplace?.region_name);

  const avatar = (
    <span
      className={cn(
        'sk:relative sk:inline-flex sk:shrink-0',
        size === 'lg' ? 'sk:size-9' : 'sk:size-8',
        isDimmed && 'sk:grayscale',
        className,
      )}
    >
      <span
        className={cn(
          'sk:flex sk:size-full sk:items-center sk:justify-center sk:overflow-hidden',
          'sk:rounded-lg sk:inset-shadow-hairline sk:bg-surface',
        )}
      >
        {shown ? (
          <img
            src={shown}
            alt=""
            className={cn(
              'sk:object-contain',
              size === 'lg' ? 'sk:size-5.5' : 'sk:size-4.5',
            )}
            onError={() => setFailed(true)}
          />
        ) : (
          <span
            className={cn(
              'sk:font-semibold sk:text-fg-secondary',
              size === 'lg' ? 'sk:text-xs' : 'sk:text-[10px]',
            )}
          >
            {initial(
              fallbackText,
              marketplace?.type,
              currentOrgInfo?.company_name,
            )}
          </span>
        )}
      </span>

      {region ? (
        <img
          src={region}
          alt={marketplace?.region_name}
          className={cn(
            'sk:absolute sk:-right-1 sk:-bottom-0.5 sk:rounded-xs',
            size === 'lg' ? 'sk:h-3 sk:w-4' : 'sk:h-2.5 sk:w-3.5',
            'sk:object-cover sk:ring-1 sk:ring-surface',
          )}
        />
      ) : null}
    </span>
  );

  if (!shopName) {
    return avatar;
  }

  return (
    <Tooltip content={shopName} placement="bottom start">
      {avatar}
    </Tooltip>
  );
}
